import { memo, useMemo } from 'react'
import { useLocation } from 'react-router'
import { NodeProps, useReactFlow } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { NodeTypeEnum, PanelEnum } from '@ui/interfaces'
import { useNavigateToNode } from '@ui/lib/hooks/useNavigateToNode'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { useCodeNodeStore, useNodeAndEdgeStore } from '@ui/stores'
import { useLayoutPanelManagerStore } from '@ui/stores/pipelinesWorkflow'

import { LayoutPanel, RIGHT_PANEL_CONTENT } from '../PipelineLayoutManager'
import { NodeContainer } from './NodeContainer'
import { NodeContainerV2 } from './NodeContainerV2'

export const CodeNode = memo((node: NodeProps) => {
  const workflowUpdateEnabled = useFeatureFlag(FeatureFlagName.WorkflowUpdate)

  const { fitView } = useReactFlow()
  const location = useLocation()
  const navigateToNode = useNavigateToNode()

  const { setDisplayModal, currentNodeId, setCurrentNodeId, nodes } = useNodeAndEdgeStore(
    useShallow((state) => ({
      displayModal: state.displayModal,
      setDisplayModal: state.setDisplayModal,
      currentNodeId: state.currentNodeId,
      setCurrentNodeId: state.setCurrentNodeId,
      nodes: state.nodes,
    }))
  )

  const { setPanelContent } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      setPanelContent: state.setPanelContent,
    }))
  )

  const setCode = useCodeNodeStore(useShallow((state) => state.setCode))

  const nodeIsSelected = useMemo(() => currentNodeId === node.id, [currentNodeId, node.id])

  const onClick = () => {
    setCode(node.data.code)
    setCurrentNodeId(node.id)

    const currentNode = nodes.find((n) => n.id === node.id)
    fitView({ nodes: [currentNode!], duration: 400, maxZoom: 1.5, padding: 0.5 })

    if (location.pathname.match(/pipelines/)) {
      setDisplayModal(PanelEnum.CODE)

      if (workflowUpdateEnabled) {
        setPanelContent({
          panel: LayoutPanel.RIGHT_PANEL,
          content: RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE,
          ignoreMaxConstraints: true,
          open: true,
        })
        setPanelContent({
          panel: LayoutPanel.INNER_RIGHT_PANEL,
          content: null,
          open: false,
        })
        navigateToNode(node.id)
      }
    }
  }

  if (workflowUpdateEnabled) {
    return (
      <NodeContainerV2 nodeIsSelected={nodeIsSelected} nodeType={NodeTypeEnum.CODE} node={node} onClick={onClick} />
    )
  } else {
    return <NodeContainer nodeIsSelected={nodeIsSelected} nodeType={NodeTypeEnum.CODE} node={node} onClick={onClick} />
  }
})
